import { useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import { GetGlChartOfAccountsDetailsList } from '@store/Finance/ChartAccounts/ChartAccountsDetailsSlice';
import type { ChartDetiles } from '@store/Finance/ChartAccounts/ChartAccountsModels';

type UseGlChartLookupsArgs = {
  autoFetch?: boolean;
  accountsOnlyLevel?: number | 'max'; // الافتراضي max
  accountsStartsWith?: string; // مثال "5"
};

const ROOT_COMPANY_ID = '69b480f1-f40b-4e77-9e66-9b75d98395a7';
const ROOT_COSTCENTER_ID = '7a5a0a0d-00d9-4b8f-9f6a-8ebe1f851b47';

const useGlChartLookups = (args: UseGlChartLookupsArgs = {}) => {
  const { autoFetch = true, accountsOnlyLevel = 'max', accountsStartsWith } = args;

  const { i18n } = useTranslation();
  const dispatch = useAppDispatch();

  const { ChartDetiles, ChartAccountsDetails, loading, error } = useAppSelector(state => state.ChartAccountsDetails);

  // ✅ dispatch واحد فقط
  useEffect(() => {
    if (!autoFetch) return;
    if ((ChartDetiles?.length ?? 0) === 0 && !loading) {
      dispatch(GetGlChartOfAccountsDetailsList());
    }
  }, [autoFetch, ChartDetiles?.length, loading, dispatch]);

  const isEn = i18n.language === 'en';
  const maxLevel = ChartAccountsDetails?.maxLevel ?? 0;

  // ===== Lists =====
  const Companies = useMemo(() => (ChartDetiles || []).filter(x => x.accountType === 'Company'), [ChartDetiles]);
  const Branches = useMemo(() => (ChartDetiles || []).filter(x => x.accountType === 'Branch'), [ChartDetiles]);
  const CostCenters = useMemo(
    () => (ChartDetiles || []).filter(x => x.accountType === 'CostCenter' && x.accountParant !== null),
    [ChartDetiles]
  );

  const Accounts = useMemo<ChartDetiles[]>(() => {
    const list = ChartDetiles || [];
    if (!list.length) return [];

    const level = accountsOnlyLevel === 'max' ? maxLevel : accountsOnlyLevel;
    if (!level) return list;

    return list.filter(x => x.accountlevel === level);
  }, [ChartDetiles, accountsOnlyLevel, maxLevel]);

  // ===== Options helper =====
  const makeOption = useCallback(
    (a: any) => ({
      value: a.id,
      label: `${isEn ? a.name : a.name2} | ${a.accountNumber}`,
      accountParant: a.accountParant,
      accountNumber: a.accountNumber,
      name: a.name,
      name2: a.name2,
      accountShow: (
        <div className='d-flex justify-content-between'>
          <div>{isEn ? a.name : a.name2}</div>
          <div className='text-theme ms-2'>{a.accountNumber}</div>
        </div>
      )
    }),
    [isEn]
  );

  const OptionsCompanies = useMemo(() => Companies.map(makeOption), [Companies, makeOption]);
  const OptionsBranches = useMemo(() => Branches.map(makeOption), [Branches, makeOption]);
  const OptionsCostCenters = useMemo(() => CostCenters.map(makeOption), [CostCenters, makeOption]);

  const OptionsAccounts = useMemo(() => Accounts.map(makeOption), [Accounts, makeOption]);

  const OptionsAccountsStartWith5 = useMemo(
    () => OptionsAccounts.filter(o => (o.accountNumber ?? '').startsWith('5')),
    [OptionsAccounts]
  );

  const OptionsAccountsFiltered = useMemo(() => {
    if (!accountsStartsWith) return OptionsAccounts;
    return OptionsAccounts.filter(o => (o.accountNumber ?? '').startsWith(accountsStartsWith));
  }, [OptionsAccounts, accountsStartsWith]);

  // ===== Trees =====
  const buildTree = useCallback(
    (list: any[], parentId: any, disableType?: string): any[] => {
      return list
        ?.filter((item: any) => item.accountParant === parentId)
        ?.map((item: any) => ({
          title: disableType
            ? isEn
              ? item.name
              : item.name2
            : `${isEn ? item.name : item.name2} | ${item.accountNumber}`,
          value: item.id,
          accountNumber: item.accountNumber,
          disabled: disableType ? item.accountType === disableType : false,
          children: buildTree(list, item.id, disableType)
        }));
    },
    [isEn]
  );

  const TreeCompanies = useMemo(
    () => buildTree(ChartDetiles || [], ROOT_COMPANY_ID, 'Branch'),
    [ChartDetiles, buildTree]
  );
  const TreeBranches = useMemo(
    () => buildTree(ChartDetiles || [], ROOT_COMPANY_ID, 'Company'),
    [ChartDetiles, buildTree]
  );
  const TreeCostCenters = useMemo(() => buildTree(ChartDetiles || [], ROOT_COSTCENTER_ID), [ChartDetiles, buildTree]);

  // ===== Code Combination =====
  const getAccountNumberById = useCallback(
    (id: any) => (ChartDetiles || []).find(x => x.id === id)?.accountNumber,
    [ChartDetiles]
  );

  const GetCodeCombination = useCallback(
    (GlCompanyId: any, GlBrancheId: any, AccountId: any, costCenterId: any) => {
      const CompanyNumber = getAccountNumberById(GlCompanyId);
      const BranchNumber = getAccountNumberById(GlBrancheId);
      const AccountNumber = getAccountNumberById(AccountId);
      const CostCenterNumber = getAccountNumberById(costCenterId);

      return [
        CompanyNumber || '00',
        BranchNumber || '0000',
        AccountNumber || '00000000',
        CostCenterNumber || '00000'
      ].join('  -  ');
    },
    [getAccountNumberById]
  );

  const GetCodeCombinationDetails = useCallback(
    (GlCompanyId: any, GlBrancheId: any, AccountId: any, costCenterId: any) => {
      const company = OptionsCompanies?.find(f => f.value === GlCompanyId)?.accountShow;
      const branch = OptionsBranches?.find(f => f.value === GlBrancheId)?.accountShow;
      const account = OptionsAccounts?.find(f => f.value === AccountId)?.accountShow;
      const cost = OptionsCostCenters?.find(f => f.value === costCenterId)?.accountShow;

      const fallback = (num: string) => (
        <div className='d-flex justify-content-between'>
          <div>########</div>
          <div className='text-theme ms-2'>{num}</div>
        </div>
      );

      return (
        <div className='text-start'>
          <p className='m-0'>{company || fallback('00')}</p>
          <p className='m-0'>{branch || fallback('0000')}</p>
          <p className='m-0'>{account || fallback('00000000')}</p>
          <p className='m-0'>{cost || fallback('00000')}</p>
        </div>
      );
    },
    [OptionsCompanies, OptionsBranches, OptionsAccounts, OptionsCostCenters]
  );

  return {
    // state
    ChartDetiles,
    ChartAccountsDetails,
    maxLevel,
    loading,
    error,

    // lists
    Companies,
    Branches,
    CostCenters,
    Accounts,

    // options
    OptionsCompanies,
    OptionsBranches,
    OptionsCostCenters,
    OptionsAccounts,
    OptionsAccountsStartWith5,
    OptionsAccountsFiltered,

    // trees
    TreeCompanies,
    TreeBranches,
    TreeCostCenters,

    // helpers
    GetCodeCombination,
    GetCodeCombinationDetails
  };
};

export default useGlChartLookups;
